import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Chip,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Toolbar,
  Grid,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Visibility,
  CheckCircle,
  Cancel,
  Schedule,
  Logout,
  AccountCircle,
  FileDownload,
  FilterList,
  ContentCopy,
  Delete,
  Search,
  Clear,
} from '@mui/icons-material';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { FormSubmission, deleteFormSubmission } from '../../services/firebaseService';
import { signOutAdmin, getCurrentUser } from '../../services/authService';
import { exportToExcel, exportContactFormsToExcel } from '../../services/excelService';
import { useNavigate } from 'react-router-dom';
import { createEvent, getEvents, Event, updateEventActive, getEventResponses, EventResponse, getMeetups, createMeetup, updateMeetup, deleteMeetup, Meetup, getContactForms, updateContactFormStatus, deleteContactForm, ContactForm } from '../../services/firebaseService';
import Switch from '@mui/material/Switch';
import ListItemButton from '@mui/material/ListItemButton';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ForumIcon from '@mui/icons-material/Forum';
import AdminHeader from './AdminHeader';
import AddCommunityMemberForm from './AddCommunityMemberForm';
import Logo from '../../assets/logo.png';
import colors from '../../theme/colors';

const AdminPanel: React.FC = () => {
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const [events, setEvents] = useState<Event[]>([]);
  const [eventForm, setEventForm] = useState({ name: '', date: '', description: '' });
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState('');
  const [copiedEventId, setCopiedEventId] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [eventResponses, setEventResponses] = useState<Record<string, EventResponse[]>>({});
  const [responsesLoading, setResponsesLoading] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'events' | 'meetups' | 'contact' | 'forum'>('events');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [responses, setResponses] = useState<EventResponse[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [eventName, setEventName] = useState('');
  
  // Meetup state
  const [meetups, setMeetups] = useState<Meetup[]>([]);
  const [meetupForm, setMeetupForm] = useState({ title: '', date: '', description: '', speakerName: '', speakerRole: '', link: '', location: '', numberOfAttendees: '' });
  const [meetupLoading, setMeetupLoading] = useState(false);
  const [meetupError, setMeetupError] = useState('');
  const [editingMeetup, setEditingMeetup] = useState<Meetup | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  
  // Contact forms state
  const [contactForms, setContactForms] = useState<ContactForm[]>([]);
  const [contactFormsLoading, setContactFormsLoading] = useState(false);

  useEffect(() => {
    loadSubmissions();
    loadEvents();
    loadMeetups();
    loadContactForms();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'formSubmissions'));
      const submissionsData: FormSubmission[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as FormSubmission;
        submissionsData.push({
          ...data,
          id: doc.id,
        });
      });

      // Sort by submission date (newest first)
      submissionsData.sort((a, b) => {
        if (a.submittedAt && b.submittedAt) {
          return b.submittedAt.toDate() - a.submittedAt.toDate();
        }
        return 0;
      });

      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      setEventLoading(true);
      const eventsData = await getEvents();
      setEvents(eventsData);
    } catch (error) {
      setEventError('Failed to load events');
    } finally {
      setEventLoading(false);
    }
  };

  const loadMeetups = async () => {
    try {
      const meetupsData = await getMeetups();
      setMeetups(meetupsData);
    } catch (error) {
      console.error('Failed to load meetups:', error);
    }
  };

  const loadContactForms = async () => {
    try {
      setContactFormsLoading(true);
      const contactFormsData = await getContactForms();
      setContactForms(contactFormsData);
    } catch (error) {
      console.error('Failed to load contact forms:', error);
    } finally {
      setContactFormsLoading(false);
    }
  };

  const updateStatus = async (submissionId: string, newStatus: FormSubmission['status']) => {
    try {
      await updateDoc(doc(db, 'formSubmissions', submissionId), {
        status: newStatus,
      });
      
      // Update local state
      setSubmissions(prev => 
        prev.map(sub => 
          sub.id === submissionId 
            ? { ...sub, status: newStatus }
            : sub
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status');
    }
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteFormSubmission(submissionId);
      // Remove from local state
      setSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
      // Close expanded row if it was the deleted one
      if (expandedRow === submissionId) {
        setExpandedRow(null);
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      setError('Failed to delete submission');
    }
  };

  const handleEventFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEventForm({ ...eventForm, [e.target.name]: e.target.value });
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setEventError('');
    try {
      setEventLoading(true);
      await createEvent(eventForm);
      setEventForm({ name: '', date: '', description: '' });
      await loadEvents();
    } catch (error) {
      setEventError('Failed to create event');
    } finally {
      setEventLoading(false);
    }
  };

  const handleMeetupFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMeetupForm({ ...meetupForm, [e.target.name]: e.target.value });
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImageFile(e.target.files[0]);
    }
  };

  const handleCreateMeetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setMeetupError('');
    try {
      setMeetupLoading(true);
      await createMeetup({
        ...meetupForm,
        numberOfAttendees: meetupForm.numberOfAttendees && meetupForm.numberOfAttendees.trim() !== '' ? Number(meetupForm.numberOfAttendees) : undefined
      }, selectedImageFile || undefined);
      setMeetupForm({ title: '', date: '', description: '', speakerName: '', speakerRole: '', link: '', location: '', numberOfAttendees: '' });
      setSelectedImageFile(null);
      await loadMeetups();
    } catch (error) {
      setMeetupError('Failed to create meetup');
    } finally {
      setMeetupLoading(false);
    }
  };

  const handleEditMeetup = (meetup: Meetup) => {
    setEditingMeetup(meetup);
    setMeetupForm({
      title: meetup.title,
      date: meetup.date,
      description: meetup.description,
      speakerName: meetup.speakerName,
      speakerRole: meetup.speakerRole,
      link: meetup.link || '',
      location: meetup.location || '',
      numberOfAttendees: meetup.numberOfAttendees?.toString() || '',
    });
  };

  const handleUpdateMeetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMeetup) return;
    
    setMeetupError('');
    try {
      setMeetupLoading(true);
      await updateMeetup(editingMeetup.id!, {
        ...meetupForm,
        numberOfAttendees: meetupForm.numberOfAttendees && meetupForm.numberOfAttendees.trim() !== '' ? Number(meetupForm.numberOfAttendees) : undefined
      }, selectedImageFile || undefined);
      setEditingMeetup(null);
      setMeetupForm({ title: '', date: '', description: '', speakerName: '', speakerRole: '', link: '', location: '', numberOfAttendees: '' });
      setSelectedImageFile(null);
      await loadMeetups();
    } catch (error) {
      setMeetupError('Failed to update meetup');
    } finally {
      setMeetupLoading(false);
    }
  };

  const handleDeleteMeetup = async (meetupId: string, imageName?: string) => {
    if (window.confirm('Are you sure you want to delete this meetup?')) {
      try {
        await deleteMeetup(meetupId, imageName);
        await loadMeetups();
      } catch (error) {
        setMeetupError('Failed to delete meetup');
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingMeetup(null);
    setMeetupForm({ title: '', date: '', description: '', speakerName: '', speakerRole: '', link: '', location: '', numberOfAttendees: '' });
    setSelectedImageFile(null);
  };

  const handleUpdateContactStatus = async (contactId: string, newStatus: ContactForm['status']) => {
    try {
      await updateContactFormStatus(contactId, newStatus);
      await loadContactForms();
    } catch (error) {
      console.error('Error updating contact form status:', error);
    }
  };

  const handleDeleteContactForm = async (contactId: string) => {
    if (window.confirm('Are you sure you want to delete this contact form?')) {
      try {
        await deleteContactForm(contactId);
        await loadContactForms();
      } catch (error) {
        console.error('Error deleting contact form:', error);
      }
    }
  };

  const getStatusColor = (status: FormSubmission['status']) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'reviewed':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: FormSubmission['status']) => {
    switch (status) {
      case 'approved':
        return <CheckCircle fontSize="small" />;
      case 'rejected':
        return <Cancel fontSize="small" />;
      case 'reviewed':
        return <Visibility fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };

  const handleLogout = async () => {
    try {
      await signOutAdmin();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      setError('Failed to logout');
    }
  };

  const handleExportAll = () => {
    exportToExcel(submissions);
  };

  const handleExportFiltered = () => {
    const suffix = statusFilter !== 'all' ? `-${statusFilter}` : searchQuery.trim() ? '-filtered' : '';
    exportToExcel(filteredSubmissions, `syrdevuae-submissions${suffix}`);
  };

  const hasActiveFilters = statusFilter !== 'all' || searchQuery.trim() !== '';

  const matchesSearch = (sub: FormSubmission, query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const qNormalized = q.replace(/\s/g, '');
    const name = (sub.name || '').toLowerCase();
    const phone = (sub.phoneNumber || '').replace(/\s/g, '').toLowerCase();
    const email = (sub.email || '').toLowerCase();
    return name.includes(q) || phone.includes(qNormalized) || email.includes(q);
  };

  const filteredSubmissions = submissions.filter(sub => {
    const statusMatch = statusFilter === 'all' || sub.status === statusFilter;
    return statusMatch && matchesSearch(sub, searchQuery);
  });

  // Calculate skills statistics
  const allSkills = submissions.flatMap(sub => sub.skills || []);
  const skillsCount = allSkills.reduce((acc, skill) => {
    acc[skill] = (acc[skill] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const topSkills = Object.entries(skillsCount)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([skill, count]) => ({ skill, count }));

  const handleToggleActive = async (eventId: string, currentActive: boolean) => {
    await updateEventActive(eventId, !currentActive);
    await loadEvents();
  };

  const handleExpandEvent = async (eventId: string) => {
    if (expandedEventId === eventId) {
      setExpandedEventId(null);
      return;
    }
    setExpandedEventId(eventId);
    if (!eventResponses[eventId]) {
      setResponsesLoading(eventId);
      try {
        const responses = await getEventResponses(eventId);
        setEventResponses(prev => ({ ...prev, [eventId]: responses }));
      } finally {
        setResponsesLoading(null);
      }
    }
  };

  const handleShowResponses = async (eventId: string) => {
    setSelectedEventId(eventId);
    setDetailsLoading(true);
    try {
      // Fetch event name
      const eventDoc = await import('../../firebase').then(({ db }) =>
        import('firebase/firestore').then(({ doc, getDoc }) => getDoc(doc(db, 'events', eventId)))
      );
      if (eventDoc && eventDoc.exists()) {
        setEventName(eventDoc.data().name || 'Event');
      }
      const res = await getEventResponses(eventId);
      setResponses(res);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleBackToEvents = () => {
    setSelectedEventId(null);
    setResponses([]);
    setEventName('');
  };

  const exportResponsesToExcel = (responses: EventResponse[], eventName: string) => {
    const csv = [
      ['Name', 'Email', 'LinkedIn', 'Country', 'Speciality', 'Submitted'],
      ...responses.map(r => [
        r.name, r.email, r.linkedin, r.country, r.speciality, r.submittedAt?.toDate ? r.submittedAt.toDate().toLocaleString() : ''
      ])
    ].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${eventName || 'event'}-responses.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar Navigation */}
      <Drawer
        variant="permanent"
        sx={{
          width: 220,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: 220,
            boxSizing: 'border-box',
            background: '#D9D9D9',
            color: '#000',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, mb: 2 }}>
          <img src={Logo} alt="SyrDevUae Logo" style={{ width: 140, marginBottom: 12 }} />
          <Button
            variant="contained"
            color="primary"
            sx={{ mb: 2, width: '90%' }}
            onClick={() => window.open('/', '_blank')}
          >
            Go to Site
          </Button>
        </Box>
        <Box sx={{ overflow: 'auto' }}>
          <List>
            <ListItem disablePadding>
              <ListItemButton selected={selectedTab === 'events'} onClick={() => setSelectedTab('events')}
                sx={{ color: '#000', '&.Mui-selected': { background: 'rgba(255,255,255,0.08)' } }}>
                <ListItemIcon sx={{ color: '#000' }}><EventNoteIcon /></ListItemIcon>
                <ListItemText primary="Events" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton selected={selectedTab === 'meetups'} onClick={() => setSelectedTab('meetups')}
                sx={{ color: '#000', '&.Mui-selected': { background: 'rgba(255,255,255,0.08)' } }}>
                <ListItemIcon sx={{ color: '#000' }}><EventNoteIcon /></ListItemIcon>
                <ListItemText primary="Meetups" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton selected={selectedTab === 'contact'} onClick={() => setSelectedTab('contact')}
                sx={{ color: '#000', '&.Mui-selected': { background: 'rgba(255,255,255,0.08)' } }}>
                <ListItemIcon sx={{ color: '#000' }}><EventNoteIcon /></ListItemIcon>
                <ListItemText primary="Contact Forms" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton selected={selectedTab === 'forum'} onClick={() => setSelectedTab('forum')}
                sx={{ color: '#000', '&.Mui-selected': { background: 'rgba(255,255,255,0.08)' } }}>
                <ListItemIcon sx={{ color: '#000' }}><ForumIcon /></ListItemIcon>
                <ListItemText primary="Community Forum" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: '#f7f9fb', minHeight: '100vh', color: '#222428' }}>
        <AdminHeader email={currentUser?.email ?? undefined} onLogout={handleLogout} />
        {selectedTab === 'events' && (
          <>
            {selectedEventId ? (
              // details view
              <Box sx={{ p: 2, background: 'transparent', color: '#222428' }}>
                <Button variant="outlined" onClick={handleBackToEvents} sx={{ mb: 2 }}>
                  Back
                </Button>
                <Typography variant="h4" sx={{ color: '#222428', mb: 2 }}>{eventName} - Responses</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownload />}
                  onClick={() => exportResponsesToExcel(responses, eventName)}
                  sx={{ mb: 2 }}
                  disabled={responses.length === 0}
                >
                  Export Responses
                </Button>
                {detailsLoading ? (
                  <CircularProgress />
                ) : responses.length === 0 ? (
                  <Typography>No responses yet.</Typography>
                ) : (
                  <TableContainer sx={{ background: 'transparent', color: '#222428' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#222428' }}>Name</TableCell>
                          <TableCell sx={{ color: '#222428' }}>Email</TableCell>
                          <TableCell sx={{ color: '#222428' }}>LinkedIn</TableCell>
                          <TableCell sx={{ color: '#222428' }}>Country</TableCell>
                          <TableCell sx={{ color: '#222428' }}>Speciality</TableCell>
                          <TableCell sx={{ color: '#222428' }}>Submitted</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {responses.map(resp => (
                          <TableRow key={resp.id}>
                            <TableCell sx={{ color: '#222428' }}>{resp.name}</TableCell>
                            <TableCell sx={{ color: '#222428' }}>{resp.email}</TableCell>
                            <TableCell sx={{ color: '#222428' }}>{resp.linkedin}</TableCell>
                            <TableCell sx={{ color: '#222428' }}>{resp.country}</TableCell>
                            <TableCell sx={{ color: '#222428' }}>{resp.speciality}</TableCell>
                            <TableCell sx={{ color: '#222428' }}>{resp.submittedAt?.toDate ? resp.submittedAt.toDate().toLocaleString() : ''}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : (
              // event list/creation UI
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#222428' }}>
                    SyrDevUae Admin Panel
                  </Typography>
                </Box>
                {/* Event Creation Section */}
                <Box sx={{ p: 2, mb: 3, background: 'transparent', color: '#222428' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#222428' }}>
                    Create New Event
                  </Typography>
                  <form onSubmit={handleCreateEvent} style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="text"
                      name="name"
                      placeholder="Event Name"
                      value={eventForm.name}
                      onChange={handleEventFormChange}
                      required
                      style={{ padding: 8, minWidth: 180 }}
                    />
                    <input
                      type="date"
                      name="date"
                      placeholder="Date"
                      value={eventForm.date}
                      onChange={handleEventFormChange}
                      required
                      style={{ padding: 8, minWidth: 140 }}
                    />
                    <input
                      type="text"
                      name="description"
                      placeholder="Description"
                      value={eventForm.description}
                      onChange={handleEventFormChange}
                      required
                      style={{ padding: 8, minWidth: 220 }}
                    />
                    <Button type="submit" variant="contained" color="primary" disabled={eventLoading}>
                      {eventLoading ? 'Creating...' : 'Create Event'}
                    </Button>
                  </form>
                  {eventError && <Alert severity="error" sx={{ mt: 2 }} />}
                </Box>
                {/* Event List Section */}
                <Box sx={{ p: 2, mb: 3, background: 'transparent', color: '#222428' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#222428' }}>
                    Events
                  </Typography>
                  {eventLoading ? (
                    <CircularProgress />
                  ) : (
                    <ul>
                      {events.map(event => (
                        <li key={event.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>
                              <strong style={{ color: '#222428' }}>{event.name}</strong> <span style={{ color: '#222428' }}>({event.date}) - {event.description}</span>
                            </span>
                            <Switch
                              checked={event.active}
                              onChange={() => handleToggleActive(event.id!, event.active)}
                              color="primary"
                              inputProps={{ 'aria-label': 'Activate/Deactivate Event' }}
                            />
                            <Typography variant="caption" sx={{ color: event.active ? '#3A685C' : '#B00020' }}>
                              {event.active ? 'Active' : 'Inactive'}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ContentCopy />}
                              onClick={async () => {
                                await navigator.clipboard.writeText(`${window.location.origin}/event/${event.id}`);
                                setCopiedEventId(event.id || null);
                                setTimeout(() => setCopiedEventId(null), 2000);
                              }}
                            >
                              Copy Form Link
                            </Button>
                            {copiedEventId === event.id && (
                              <Typography variant="caption" sx={{ color: '#3A685C', ml: 1 }}>
                                Link copied!
                              </Typography>
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleShowResponses(event.id!)}
                              sx={{ ml: 1 }}
                            >
                              Show Responses
                            </Button>
                          </Box>
                        </li>
                      ))}
                    </ul>
                  )}
                </Box>
              </>
            )}
          </>
        )}
        {selectedTab === 'meetups' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#222428' }}>
                Meetups Management
              </Typography>
            </Box>
            
            {/* Meetup Creation/Edit Section */}
            <Box sx={{ p: 2, mb: 3, background: 'transparent', color: '#222428' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#222428' }}>
                {editingMeetup ? 'Edit Meetup' : 'Create New Meetup'}
              </Typography>
              <form onSubmit={editingMeetup ? handleUpdateMeetup : handleCreateMeetup} style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
                <Box sx={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    name="title"
                    placeholder="Meetup Title"
                    value={meetupForm.title}
                    onChange={handleMeetupFormChange}
                    required
                    style={{ padding: 8, minWidth: 200, flex: 1 }}
                  />
                  <input
                    type="date"
                    name="date"
                    placeholder="Date"
                    value={meetupForm.date}
                    onChange={handleMeetupFormChange}
                    required
                    style={{ padding: 8, minWidth: 140 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    name="speakerName"
                    placeholder="Speaker Name"
                    value={meetupForm.speakerName}
                    onChange={handleMeetupFormChange}
                    required
                    style={{ padding: 8, minWidth: 200, flex: 1 }}
                  />
                  <input
                    type="text"
                    name="speakerRole"
                    placeholder="Speaker Role/Company"
                    value={meetupForm.speakerRole}
                    onChange={handleMeetupFormChange}
                    required
                    style={{ padding: 8, minWidth: 200, flex: 1 }}
                  />
                </Box>
                <textarea
                  name="description"
                  placeholder="Description"
                  value={meetupForm.description}
                  onChange={handleMeetupFormChange}
                  required
                  style={{ padding: 8, minHeight: 80, resize: 'vertical' }}
                />
                <input
                  type="url"
                  name="link"
                  placeholder="Link (optional)"
                  value={meetupForm.link}
                  onChange={handleMeetupFormChange}
                  style={{ padding: 8 }}
                />
                <Box sx={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    name="location"
                    placeholder="Location"
                    value={meetupForm.location}
                    onChange={handleMeetupFormChange}
                    style={{ padding: 8, minWidth: 200, flex: 1 }}
                  />
                  <input
                    type="number"
                    name="numberOfAttendees"
                    placeholder="Number of Attendees"
                    value={meetupForm.numberOfAttendees}
                    onChange={handleMeetupFormChange}
                    min="0"
                    style={{ padding: 8, minWidth: 150 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    style={{ padding: 8 }}
                  />
                  {selectedImageFile && (
                    <Typography variant="body2" sx={{ color: colors.success }}>
                      Image selected: {selectedImageFile.name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button type="submit" variant="contained" color="primary" disabled={meetupLoading}>
                    {meetupLoading ? 'Saving...' : (editingMeetup ? 'Update Meetup' : 'Create Meetup')}
                  </Button>
                  {editingMeetup && (
                    <Button variant="outlined" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  )}
                </Box>
                {meetupError && <Alert severity="error" sx={{ mt: 2 }}>{meetupError}</Alert>}
              </form>
            </Box>

            {/* Meetups List Section */}
            <Box sx={{ p: 2, mb: 3, background: 'transparent', color: '#222428' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#222428' }}>
                Meetups ({meetups.length})
              </Typography>
              {meetups.length === 0 ? (
                <Typography sx={{ color: colors.textSecondary }}>
                  No meetups created yet. Create your first meetup above.
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                  {meetups.map((meetup) => (
                    <Paper key={meetup.id} sx={{ p: 2, background: colors.paper }}>
                      {meetup.imageUrl && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                          <img
                            src={meetup.imageUrl}
                            alt={meetup.title}
                            style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'cover', borderRadius: 8 }}
                          />
                        </Box>
                      )}
                                              <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
                          {meetup.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                          {new Date(meetup.date).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.accent, mb: 1, fontWeight: 600 }}>
                          {meetup.speakerName} • {meetup.speakerRole}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                          {meetup.description}
                        </Typography>
                      {meetup.location && (
                        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                          <strong>Location:</strong> {meetup.location}
                        </Typography>
                      )}
                      {meetup.numberOfAttendees && (
                        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                          <strong>Number of Attendees:</strong> {meetup.numberOfAttendees}
                        </Typography>
                      )}
                      {meetup.link && (
                        <Typography variant="body2" sx={{ color: colors.green, mb: 2 }}>
                          <a href={meetup.link} target="_blank" rel="noopener noreferrer">
                            {meetup.link}
                          </a>
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleEditMeetup(meetup)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteMeetup(meetup.id!, meetup.imageName)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
        {selectedTab === 'contact' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#222428' }}>
                Contact Forms Management
              </Typography>
            </Box>
            
            {/* Contact Forms List */}
            <Box sx={{ p: 2, mb: 3, background: 'transparent', color: '#222428' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#222428' }}>
                  Contact Forms ({contactForms.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  onClick={() => exportContactFormsToExcel(contactForms)}
                  color="primary"
                  disabled={contactForms.length === 0}
                >
                  Export to Excel
                </Button>
              </Box>
              {contactFormsLoading ? (
                <CircularProgress />
              ) : contactForms.length === 0 ? (
                <Typography sx={{ color: colors.textSecondary }}>
                  No contact forms submitted yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 2 }}>
                  {contactForms.map((contactForm) => (
                    <Paper key={contactForm.id} sx={{ p: 3, background: colors.paper }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
                            {contactForm.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                            {contactForm.email}
                          </Typography>
                          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                            {contactForm.submittedAt?.toDate ? 
                              new Date(contactForm.submittedAt.toDate()).toLocaleString() : 
                              'Date not available'
                            }
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={contactForm.status}
                            color={
                              contactForm.status === 'new' ? 'warning' :
                              contactForm.status === 'read' ? 'info' :
                              'success'
                            }
                            size="small"
                          />
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" sx={{ color: colors.textPrimary, mb: 3, whiteSpace: 'pre-wrap' }}>
                        {contactForm.message}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label="Mark as Read"
                            color="info"
                            onClick={() => handleUpdateContactStatus(contactForm.id!, 'read')}
                            disabled={contactForm.status !== 'new'}
                            size="small"
                            clickable
                          />
                          <Chip
                            label="Mark as Replied"
                            color="success"
                            onClick={() => handleUpdateContactStatus(contactForm.id!, 'replied')}
                            disabled={contactForm.status === 'replied'}
                            size="small"
                            clickable
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteContactForm(contactForm.id!)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
        {selectedTab === 'forum' && (
          <>
            <AddCommunityMemberForm onAdded={loadSubmissions} />
            <Box sx={{ p: 2, mb: 2, background: 'transparent', color: '#222428' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, phone number, or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Clear search">
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ maxWidth: 480 }}
              />
            </Box>
            {/* Export and Filter Controls */}
            <Box sx={{ p: 2, mb: 3, background: 'transparent', color: '#222428' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#222428' }}>
                Export Data
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportAll}
                color="primary"
              >
                Export All ({submissions.length})
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={handleExportFiltered}
                disabled={!hasActiveFilters}
              >
                Export Filtered ({filteredSubmissions.length})
              </Button>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ ml: 2 }}
              >
                Filters
              </Button>
              <Collapse in={showFilters}>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: '#222428' }}>Status Filter:</Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="reviewed">Reviewed</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" sx={{ color: '#222428' }}>
                    Showing {filteredSubmissions.length} of {submissions.length} submissions
                  </Typography>
                </Box>
              </Collapse>
            </Box>

            {/* Skills Summary */}
            {submissions.length > 0 && (
              <Box sx={{ p: 2, mb: 3, background: 'transparent', color: '#222428' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#222428' }}>
                  Skills Summary
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#222428' }}>
                      Total Skills Mentioned: {allSkills.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#222428' }}>
                      Unique Skills: {Object.keys(skillsCount).length}
                    </Typography>
                  </Box>
                  {topSkills.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ color: '#222428' }} gutterBottom>
                        Top Skills:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {topSkills.map(({ skill, count }) => (
                          <Chip
                            key={skill}
                            label={`${skill} (${count})`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Main Submissions Table */}
            <Box sx={{ background: 'transparent', color: '#222428' }}>
              <TableContainer sx={{ background: 'transparent' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#222428' }}>Name</TableCell>
                      <TableCell sx={{ color: '#222428' }}>Phone</TableCell>
                      <TableCell sx={{ color: '#222428' }}>Email</TableCell>
                      <TableCell sx={{ color: '#222428' }}>Education Level</TableCell>
                      <TableCell sx={{ color: '#222428' }}>Skills Count</TableCell>
                      <TableCell sx={{ color: '#222428' }}>Status</TableCell>
                      <TableCell sx={{ color: '#222428' }}>Submitted</TableCell>
                      <TableCell sx={{ color: '#222428' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSubmissions.map((submission) => (
                      <React.Fragment key={submission.id || 'temp-key'}>
                        <TableRow>
                          <TableCell sx={{ color: '#222428' }}>{submission.name}</TableCell>
                          <TableCell sx={{ color: '#222428' }}>{submission.phoneNumber || '—'}</TableCell>
                          <TableCell sx={{ color: '#222428' }}>{submission.email || '—'}</TableCell>
                          <TableCell sx={{ color: '#222428' }}>{submission.educationLevel || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={`${(submission.skills || []).length} skills`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(submission.status)}
                              label={submission.status}
                              color={getStatusColor(submission.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#222428' }}>
                            {submission.submittedAt?.toDate().toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => setExpandedRow(
                                expandedRow === submission.id ? null : (submission.id || null)
                              )}
                            >
                              {expandedRow === submission.id ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                            <Collapse in={expandedRow === (submission.id || null)} timeout="auto" unmountOnExit>
                              <Card sx={{ m: 2, background: 'transparent', color: '#222428' }}>
                                <CardContent>
                                  <Typography variant="h6" gutterBottom sx={{ color: '#222428' }}>
                                    Full Submission Details
                                  </Typography>
                                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ color: '#222428' }}>Personal Information</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Name:</strong> {submission.name}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Birth Year:</strong> {submission.birthYear || 'N/A'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Emirates of Residence:</strong> {submission.emiratesOfResidence || 'N/A'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Referred By:</strong> {submission.referredBy || 'N/A'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Phone:</strong> {submission.phoneNumber}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>LinkedIn:</strong> {submission.linkedinAccount}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Email:</strong> {submission.email}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ color: '#222428' }}>Academic Information</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Education:</strong> {submission.educationLevel}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Field:</strong> {submission.otherFieldOfStudy || submission.fieldOfStudy}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>University:</strong> {submission.universityName}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Courses:</strong> {submission.coursesCompleted || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ color: '#222428' }}>Professional Information</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Position:</strong> {submission.currentPosition || 'N/A'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Company:</strong> {submission.companyName || 'N/A'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Looking for Job:</strong> {submission.lookingForJob ? 'Yes' : 'No'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Journey:</strong> {submission.professionalJourney || 'N/A'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Community Helped with Income/Employment:</strong> {submission.communityHelpedEmployment || 'N/A'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Skills:</strong> {(submission.skills || []).length ? (submission.skills || []).join(', ') : 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ color: '#222428' }}>Additional Information</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Interested in Speaking:</strong> {submission.interestedInSpeaking || 'N/A'}</Typography>
                                      {submission.interestedInSpeaking === 'Yes' && submission.speakingTopics && (
                                        <Typography sx={{ color: '#222428' }}><strong>Speaking Topics:</strong> {submission.speakingTopics}</Typography>
                                      )}
                                      <Typography sx={{ color: '#222428' }}><strong>Interested in Entrepreneurship:</strong> {submission.interestedInEntrepreneurship || 'N/A'}</Typography>
                                      <Typography sx={{ color: '#222428' }}><strong>Has Project/Idea:</strong> {submission.hasProjectIdea || 'N/A'}</Typography>
                                      {submission.hasProjectIdea === 'Yes' && submission.projectIdeaDescription && (
                                        <Typography sx={{ color: '#222428' }}><strong>Project/Idea Description:</strong> {submission.projectIdeaDescription}</Typography>
                                      )}
                                      <Typography sx={{ color: '#222428' }}><strong>Interested in Volunteering:</strong> {submission.interestedInVolunteering || 'N/A'}</Typography>
                                      {submission.interestedInVolunteering === 'Yes' && submission.volunteerTeam && (
                                        <Typography sx={{ color: '#222428' }}><strong>Volunteer Team:</strong> {submission.volunteerTeam}</Typography>
                                      )}
                                      {submission.generalNote && (
                                        <Typography sx={{ color: '#222428' }}><strong>General Note:</strong> {submission.generalNote}</Typography>
                                      )}
                                    </Box>
                                  </Box>
                                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Chip
                                      label="Approve"
                                      color="success"
                                      onClick={() => submission.id && updateStatus(submission.id, 'approved')}
                                      disabled={submission.status === 'approved' || !submission.id}
                                    />
                                    <Chip
                                      label="Review"
                                      color="warning"
                                      onClick={() => submission.id && updateStatus(submission.id, 'reviewed')}
                                      disabled={submission.status === 'reviewed' || !submission.id}
                                    />
                                    <Chip
                                      label="Reject"
                                      color="error"
                                      onClick={() => submission.id && updateStatus(submission.id, 'rejected')}
                                      disabled={submission.status === 'rejected' || !submission.id}
                                    />
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      startIcon={<Delete />}
                                      onClick={() => submission.id && handleDeleteSubmission(submission.id)}
                                      sx={{ ml: 'auto' }}
                                    >
                                      Delete
                                    </Button>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminPanel; 